"use client";

import { useEffect } from "react";
import { useUser } from "@clerk/nextjs";
import { setAnalyticsUserId, trackAccountLead } from "@/lib/analytics";

/* An account counts as new for this long after Clerk created it. Long enough
   for the Google round-trip and a slow OTP email; short enough that a returning
   user signing in on a fresh browser is not counted again. */
const NEW_ACCOUNT_WINDOW_MS = 30 * 60 * 1000;

/* The pixel may still be loading when the account appears (the OAuth return
   lands on a fresh page). Wait for it briefly rather than lose the Lead. */
const PIXEL_WAIT_MS = 15_000;
const PIXEL_POLL_MS = 500;

/**
 * Sets the GA4 User-ID for signed-in visitors, and fires the account Lead once,
 * for every way an account gets created on the landing: the email code in the
 * recommendation gate and booking modals, and
 * Google sign-in, which leaves the page and returns through `/sso-callback`
 * with the session already set, so no call site ever sees it happen. Watching
 * for a freshly created user covers all of them in one place.
 */
export default function AccountLeadTracker() {
  const { isLoaded, user } = useUser();

  // GA4 User-ID for every signed-in visitor, set before the Lead below so the
  // sign_up event already carries it.
  useEffect(() => {
    if (!isLoaded) return;
    setAnalyticsUserId(user?.id ?? null);
  }, [isLoaded, user?.id]);

  useEffect(() => {
    if (!isLoaded || !user?.createdAt) return;
    if (Date.now() - user.createdAt.getTime() > NEW_ACCOUNT_WINDOW_MS) return;

    const fire = () =>
      trackAccountLead({
        userId: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        method: user.externalAccounts.length > 0 ? "google" : "email",
      });

    if (typeof window.fbq === "function") {
      fire();
      return;
    }
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (typeof window.fbq === "function" || Date.now() - started > PIXEL_WAIT_MS) {
        window.clearInterval(timer);
        fire();
      }
    }, PIXEL_POLL_MS);
    return () => window.clearInterval(timer);
  }, [isLoaded, user]);

  return null;
}
